import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, SchemaOf, string } from 'yup';
import { TextInput } from '../../../../theme/TextInput';
import { emailRegExp, fullNameRegExp } from '../../utils/validationRegExp';
import { Theme } from '@material-ui/core/styles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import createStyles from '@material-ui/core/styles/createStyles';
import { useButtonStyles } from '../styles';
import FormControlLabel from '@material-ui/core/FormControlLabel/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox/Checkbox';
import ReCaptcha, { REACT_APP_SIGNUP_GOOGLE_SITE_KEY } from '../ReCaptcha';
import Button from '@material-ui/core/Button/Button';
import { useNavigate } from 'react-router-dom';
import { useInvokeApi } from '../../utils/useInvokeApi';
import CircularProgress from '@material-ui/core/CircularProgress/CircularProgress';
import { useSelector } from 'react-redux';
import { AppState } from '../../../../store';
import { ErrorText } from '../SignupTextField';
import { GivelifySnackbar } from '@givelify/givelify-ui';
import { pushEvent } from '../../../../utils/clevertapUtils';
import {
    INVITE_DONEE_EMAIL_REQUESTED,
    INVITE_DONEE_EMAIL_SENT_SUCCESSFULLY,
} from '../../../../utils/clevertapEvents';
import { yupResolver } from '@hookform/resolvers/yup';
import { normalizeApostrophes } from 'pages/signup/utils/normalizeApostrophes';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        formStyles: {
            '& .error-text': {
                marginTop: 0,
                marginBottom: theme.spacing(3),
            },
            textAlign: 'left',
            '& .MuiFormControlLabel-label': {
                fontSize: 14,
            },
            '& .checkbox': {
                marginBottom: theme.spacing(4),
            },
            '& .cancel-button': {
                marginRight: theme.spacing(3),
            },
        },
    }),
);

interface InviteDoneeFormProps {
    organizationName: string;
    officerName: string;
    officerEmail: string;
    yourName: string;
    yourEmail: string;
}

const InviteDoneeForm: React.FC<{ previous: string }> = ({ previous }) => {
    const { t } = useTranslation();
    const push = useNavigate();
    const { organizationName, uuid } = useSelector((state: AppState) => ({
        uuid: state.Signup.uuid,
        organizationName: state.Signup.organizationName,
    }));
    const [requestState, makeRequest] = useInvokeApi();
    const { formStyles } = useStyles();
    const [open, setOpen] = useState<boolean>(false);
    const [sendCopy, setSendCopy] = useState<boolean>(false);
    const { wrapper, buttonProgress } = useButtonStyles();
    const handleCancel = () => push(previous);
    const handleToggle = () => setSendCopy((c) => !c);
    const {
        emailRequired,
        organizationNameText,
        officerNameText,
        officerEmailText,
        yourNameText,
        yourEmailText,
        sendCopyText,
        cancelText,
        sendInviteText,
        successMessage,
        invalidFullName,
    } = useMemo(
        () => ({
            cancelText: t('signup.cancel'),
            sendInviteText: t('signup.inviteDonee.form.sendInvite'),

            organizationNameText: t('signup.inviteDonee.form.organizationName'),
            officerNameText: t('signup.inviteDonee.form.officerName'),
            officerEmailText: t('signup.inviteDonee.form.officerEmail'),
            yourNameText: t('signup.inviteDonee.form.yourName'),
            yourEmailText: t('signup.inviteDonee.form.yourEmail'),
            sendCopyText: t('signup.inviteDonee.form.sendCopy'),
            invalidFullName: t('error.signup.fullName'),
            required: t('error.required'),
            emailRequired: t('error.signup.email'),
            successMessage: t('signup.inviteDonee.form.successMessage'),
        }),
        [t],
    );
    const schema: SchemaOf<InviteDoneeFormProps> = object({
        organizationName: string().required('required').max(255),
        yourName: string()
            .required(invalidFullName)
            .max(255)
            .matches(fullNameRegExp, invalidFullName),
        officerName: string().required('required').max(255),
        officerEmail: string().matches(emailRegExp, emailRequired).max(255),
        yourEmail: string().matches(emailRegExp, emailRequired).max(255),
    }).required();

    const form = useForm<InviteDoneeFormProps>({
        mode: 'onBlur',
        defaultValues: {
            organizationName,
            officerName: '',
            officerEmail: '',
            yourName: '',
            yourEmail: '',
        },
        resolver: yupResolver(schema),
    });
    const handleSubmit = () => {
        const values = form.getValues();
        const grecaptcha = (window as any).grecaptcha;
        if (!grecaptcha) return;

        grecaptcha.ready(() => {
            grecaptcha
                .execute(REACT_APP_SIGNUP_GOOGLE_SITE_KEY, { action: 'submit' })
                .then((token: unknown) => {
                    pushEvent(INVITE_DONEE_EMAIL_REQUESTED, {
                        authorizedOfficerName: normalizeApostrophes(values.officerName),
                    });
                    makeRequest('POST', '/signup/official-invitation', {
                        uuid,
                        organizationName: normalizeApostrophes(values.organizationName),
                        authorizedOfficerName: normalizeApostrophes(values.officerName),
                        authorizedOfficerEmail: values.officerEmail,
                        userName: values.yourName,
                        userEmail: values.yourEmail,
                        recaptcha: token,
                    });
                });
        });
    };
    useEffect(() => {
        if (requestState.type === 'REQUEST_SUCCESS') {
            form.reset();
            setOpen(true);
            pushEvent(INVITE_DONEE_EMAIL_SENT_SUCCESSFULLY);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestState]);

    const onClose = () => setOpen(false);
    return (
        <div className={formStyles}>
            <GivelifySnackbar
                message={successMessage}
                open={open}
                autoHideDuration={5000}
                onClose={onClose}
            />
            <form onSubmit={form.handleSubmit(handleSubmit)}>
                <TextInput<InviteDoneeFormProps>
                    type="text"
                    name="organizationName"
                    label={organizationNameText}
                    placeholder={organizationNameText}
                    formRef={form}
                />
                <TextInput<InviteDoneeFormProps>
                    type="text"
                    name="officerName"
                    label={officerNameText}
                    placeholder={officerNameText}
                    formRef={form}
                />
                <TextInput<InviteDoneeFormProps>
                    type="text"
                    name="officerEmail"
                    label={officerEmailText}
                    placeholder={officerEmailText}
                    formRef={form}
                />
                <TextInput<InviteDoneeFormProps>
                    type="text"
                    name="yourName"
                    label={yourNameText}
                    placeholder={yourNameText}
                    formRef={form}
                />
                <TextInput<InviteDoneeFormProps>
                    type="text"
                    name="yourEmail"
                    label={yourEmailText}
                    placeholder={yourEmailText}
                    formRef={form}
                />
                <ReCaptcha />
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={sendCopy}
                            onChange={handleToggle}
                        />
                    }
                    label={sendCopyText}
                    className="checkbox"
                />
                {requestState.type === 'REQUEST_ERROR' && (
                    <ErrorText
                        text={requestState.error.message}
                        className="error-text"
                    />
                )}
                <div className={wrapper}>
                    <Button
                        variant="outlined"
                        className="cancel-button"
                        onClick={handleCancel}
                    >
                        {cancelText}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={!form.formState.isValid || false}
                    >
                        {sendInviteText}
                    </Button>
                    {requestState.type === 'REQUEST_START' && (
                        <CircularProgress
                            size={24}
                            className={buttonProgress}
                        />
                    )}
                </div>
            </form>
        </div>
    );
};

export default InviteDoneeForm;
